import { InputHTMLAttributes, ReactElement } from 'react'

import { CustomActionProps, TestIdProps } from 'designSystem/types/propTypes'

import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'

import PDSIcon from '../PDSIcon/PDSIcon'
import PDSText from '../PDSText/PDSText'
import PDSTooltip from '../PDSTooltip/PDSTooltip'
import INTERNALxDiv from '../internal/elements/INTERNALxDiv'
import INTERNALxInput from '../internal/elements/INTERNALxInput'
import INTERNALxLabel from '../internal/elements/INTERNALxLabel'

type TextInputVariant = 'normal' | 'purple'

type BaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> &
  CustomActionProps &
  TestIdProps

type Props = BaseProps & {
  clearValueLabel?: string
  error?: string
  isClearable?: boolean
  isRequired?: boolean
  label?: string
  tooltip?: ReactElement | string
  variant?: TextInputVariant
}

export default function PDSTextInput({
  id,
  className,
  clearValueLabel = 'Clear',
  disabled,
  error,
  isClearable = false,
  isRequired = false,
  label,
  onClear = () => null,
  testId,
  tooltip,
  value,
  variant,
  ...props
}: Props) {
  const showClear =
    isClearable && !(value === null || value === undefined || value === '')
  const disabledClass = disabled ? 'disabled' : ''

  return (
    <INTERNALxLabel
      className={`text-input-container ${disabledClass} ${variant} ${
        className ?? ''
      } `}
      testId={testId}
      width="100%"
    >
      {(label || showClear) && (
        <INTERNALxDiv
          alignItems="center"
          display="flex"
          height="2rem"
          justifyContent="space-between"
        >
          <INTERNALxDiv alignItems="center" display="flex" gap={SIZE.s8}>
            <PDSText color={COLOR.textSecondary} size="md" weight="medium">
              {label}{' '}
              {isRequired && <PDSText color={COLOR.textSecondary}>*</PDSText>}
            </PDSText>
            {tooltip && <PDSTooltip content={tooltip} />}
          </INTERNALxDiv>

          {showClear && (
            <PDSText
              className="clearable"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault()
                onClear()
              }}
              size="sm"
              textDecoration="underline"
              weight="semibold"
              testId={testId && `${testId}-clear`}
            >
              {clearValueLabel}
            </PDSText>
          )}
        </INTERNALxDiv>
      )}

      <INTERNALxInput
        border={`1px solid ${COLOR.grey400}`}
        borderRadius={SIZE.s4}
        className={`text-input ${error ? 'error' : ''}`}
        color={COLOR.textSecondary}
        data-testid={testId && `${testId}-input`}
        disabled={disabled}
        flex={1}
        fontSize={FONT_SIZE.fs14}
        height="2.75rem"
        paddingX="1rem"
        minWidth="5rem"
        type="text"
        value={value}
        width="100%"
        {...props}
      />

      {error && (
        <INTERNALxDiv
          alignItems="center"
          display="flex"
          gap={SIZE.s4}
          marginTop={SIZE.s4}
        >
          <PDSIcon color={COLOR.alert400} name="exclamationCircle" />
          <PDSText color={COLOR.alert400} size="md" weight="medium">
            {error}
          </PDSText>
        </INTERNALxDiv>
      )}
    </INTERNALxLabel>
  )
}
